"use client";

import type React from "react";

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PlusCircle,
  X,
  Pencil,
  Check,
  AlertCircle,
  Paperclip,
  Info,
} from "lucide-react";
import { getDistanceDescription } from "@/utils/format-distances";

export interface Race {
  id: string;
  name: string;
  distance: string;
}

interface RaceFormProps {
  onAddRace: (races: Race[]) => void;
  races: Race[];
}

export function RaceForm({ onAddRace, races }: RaceFormProps) {
  const [raceName, setRaceName] = useState("");
  const [raceDistance, setRaceDistance] = useState("");
  const [error, setError] = useState("");
  const [editingRaceId, setEditingRaceId] = useState<string | null>(null);
  const [distanceDescriptions, setDistanceDescriptions] = useState<{
    [key: string]: string;
  }>({});

  // Load distance descriptions with proper units based on locale
  useEffect(() => {
    // Only run in browser
    if (typeof window !== "undefined") {
      setDistanceDescriptions({
        long: getDistanceDescription("long"),
        middle: getDistanceDescription("middle"),
        olympic: getDistanceDescription("olympic"),
        sprint: getDistanceDescription("sprint"),
        other: getDistanceDescription("other"),
      });
    }
  }, []);

  const handleAddRace = async () => {
    if (!raceName || !raceDistance) {
      setError("Please fill in all required fields");
      return;
    }

    setError("");

    if (editingRaceId) {
      // Update existing race
      const updatedRaces = races.map((race) => {
        if (race.id === editingRaceId) {
          return {
            ...race,
            name: raceName,
            distance: raceDistance,
          };
        }
        return race;
      });
      onAddRace(updatedRaces);
      setEditingRaceId(null);
    } else {
      // Add new race
      const newRace: Race = {
        id: Date.now().toString(),
        name: raceName,
        distance: raceDistance,
      };
      onAddRace([...races, newRace]);
    }

    // Reset form
    setRaceName("");
    setRaceDistance("");
  };

  const handleEditRace = (race: Race) => {
    setRaceName(race.name);
    setRaceDistance(race.distance);
    setEditingRaceId(race.id);
    setError("");
  };

  const handleCancelEdit = () => {
    setRaceName("");
    setRaceDistance("");
    setEditingRaceId(null);
    setError("");
  };

  // Auto-detect race distance from race name
  useEffect(() => {
    if (raceName && !raceDistance) {
      const name = raceName.toLowerCase();

      if (
        name.includes("70.3") ||
        name.includes("half") ||
        name.includes("middle") ||
        name.includes("t100")
      ) {
        setRaceDistance("middle");
      } else if (
        (name.includes("ironman") && !name.includes("70.3")) ||
        name.includes("roth")
      ) {
        setRaceDistance("long");
      } else if (
        name.includes("olympic") ||
        name.includes("standard") ||
        name.includes("london triathlon")
      ) {
        setRaceDistance("olympic");
      } else if (name.includes("sprint") || name.includes("blenheim")) {
        setRaceDistance("sprint");
      }
    }
  }, [raceName]);

  const handleRemoveRace = (id: string) => {
    onAddRace(races.filter((race) => race.id !== id));
    if (editingRaceId === id) {
      handleCancelEdit();
    }
  };

  const getDistanceLabel = (distance: string) => {
    switch (distance) {
      case "long":
        return "Long Distance";
      case "middle":
        return "Middle Distance";
      case "olympic":
        return "Olympic Distance";
      case "sprint":
        return "Sprint Distance";
      case "other":
        return "Other Distance";
      default:
        return distance;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-medium font-spartan">
            {editingRaceId ? "Edit Race" : "Add a Race"}
          </h3>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="race-name">Race Name</Label>
              <Input
                id="race-name"
                value={raceName}
                onChange={(e) => setRaceName(e.target.value)}
                placeholder="70.3 World Champs 2024 or Challenge Roth 2023"
                className="text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddRace();
                  }
                }}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="race-distance">Race Distance</Label>
              <Select
                value={raceDistance}
                onValueChange={(value) => {
                  setRaceDistance(value);
                }}
              >
                <SelectTrigger id="race-distance">
                  <SelectValue placeholder="Select distance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="long">Long Distance (Full)</SelectItem>
                  <SelectItem value="middle">Middle Distance (Half)</SelectItem>
                  <SelectItem value="olympic">Olympic Distance</SelectItem>
                  <SelectItem value="sprint">Sprint Distance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {raceDistance && (
                <p className="text-xs text-gray-500 mt-1">
                  {distanceDescriptions[raceDistance]}
                </p>
              )}
              {raceDistance === "other" && (
                <div className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  <span>
                    Note: "Other" race types won't appear on the current quarter
                    zip design
                  </span>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="race-verification">Race Verification</Label>
              <p className="text-xs text-gray-500">
                After checkout, you will be able to upload proof of your races.
              </p>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-2">
              <Button onClick={handleAddRace} className="mt-2 flex-1">
                {editingRaceId ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Update Race
                  </>
                ) : (
                  <>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Race
                  </>
                )}
              </Button>

              {editingRaceId && (
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="mt-2"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {races.length > 0 && (
        <div className="space-y-4">
          {races.map((race) => (
            <Card key={race.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <h4 className="font-medium">{race.name}</h4>
                  <p className="text-sm text-gray-500">
                    {getDistanceLabel(race.distance)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditRace(race)}
                    className={editingRaceId === race.id ? "bg-gray-100" : ""}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveRace(race.id)}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
